// A list of notes, each expandable to its full text.
//
// WHY EXPAND RATHER THAN NAVIGATE. The list deliberately carries no bodies —
// a page of meeting write-ups is a response nobody can read — so the text is a
// second request. Making that a row you open keeps the reading where the
// context is, and means opening one note does not lose your place in the list.
//
// Shared by the deal panel and the inbox so the two cannot drift into
// different ideas of what a note looks like.

import { useState, type ReactNode } from "react";
import type { NoteListItem } from "@crm/shared";
import styles from "./NoteList.module.css";
import { Badge } from "../../design/Badge.js";
import { Spinner } from "../../design/Feedback.js";
import { useNote } from "../../api/queries.js";

/**
 * What `embedding_state` means to a reader, which is NOT what it means to the
 * system. Nobody cares that a vector exists; they care whether searching by
 * meaning will find this note. `ready` earns no badge at all — the normal case
 * should be silent.
 */
function IndexingBadge({ note }: { note: NoteListItem }): ReactNode {
  switch (note.embedding_state) {
    case "ready":
      return null;
    case "pending":
      return (
        <span title="Findable by word already; ranking by meaning follows shortly.">
          <Badge tone="neutral">indexing</Badge>
        </span>
      );
    case "failed":
      return (
        <span title={note.embedding_error ?? "Ranking by meaning is unavailable for this note."}>
          <Badge tone="warn">word search only</Badge>
        </span>
      );
  }
}

function NoteBody({ noteId }: { noteId: string }): ReactNode {
  const note = useNote(noteId);
  if (note.isPending) return <Spinner label="Loading the note…" inline />;
  if (note.data === undefined) return <p className={styles.body}>Could not load this note.</p>;
  return <p className={styles.body}>{note.data.body}</p>;
}

export interface NoteListProps {
  notes: readonly NoteListItem[];
  /** Opened on mount — how a search result links straight to the note it found. */
  initiallyOpen?: string | null;
  /** Rendered beside each row: the inbox puts its "file to a deal" control here. */
  rowActions?: (note: NoteListItem) => ReactNode;
  empty: ReactNode;
}

export function NoteList({ notes, initiallyOpen, rowActions, empty }: NoteListProps): ReactNode {
  const [open, setOpen] = useState<string | null>(initiallyOpen ?? null);

  if (notes.length === 0) return <div className={styles.empty}>{empty}</div>;

  return (
    <div className={styles.list}>
      {notes.map((note) => {
        const isOpen = open === note.id;
        return (
          <div key={note.id} className={styles.item}>
            <div className={styles.row}>
              <button
                type="button"
                className={styles.toggle}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : note.id)}
              >
                <span className={styles.chevron} aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
                <span className={styles.heading}>
                  <span className={styles.title}>{note.title ?? "Untitled note"}</span>
                  <span className={styles.meta}>
                    {/* The meeting's date, not the row's — a note imported today
                        may be about a call last month, and the row that says
                        "today" would be wrong in the way that matters. */}
                    {note.occurred_at !== null && (
                      <span>{new Date(note.occurred_at).toLocaleDateString()}</span>
                    )}
                    <span>{note.kind}</span>
                    <span>{note.source}</span>
                    {note.participants.length > 0 && (
                      <span title={note.participants.join(", ")}>
                        {note.participants.length} attending
                      </span>
                    )}
                  </span>
                </span>
                <IndexingBadge note={note} />
              </button>
              {rowActions !== undefined && (
                <div className={styles.rowActions}>{rowActions(note)}</div>
              )}
            </div>

            {note.summary !== null && !isOpen && <p className={styles.summary}>{note.summary}</p>}
            {isOpen && <NoteBody noteId={note.id} />}
          </div>
        );
      })}
    </div>
  );
}
